import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import api from '../utils/api';
import SectionHeader from '../components/SectionHeader';
import EndToEndSolutions from '../components/EndToEndSolutions';
import GoogleReviews from '../components/GoogleReviews';
import { COMPANY_FACTS, STAT_ITEMS, YEARS_ACTIVE, WARRANTY_YEARS } from '../config/companyFacts';

const API_BASE = process.env.REACT_APP_API_URL?.replace(/\/api$/,'') || '';
const imgSrc = (p) => !p ? '' : /^https?:\/\//i.test(p) ? p : `${API_BASE}${p}`;

const FALLBACK_SLIDES = [
  { image:'https://images.unsplash.com/photo-1618219908412-a29a1bb7b86e?w=1800&q=85', title:'Spaces That', titleEm:'Inspire', subtitle:'Premium interior design crafted for the way you live — in Tumakuru & across Karnataka.' },
  { image:'https://images.unsplash.com/photo-1600210492493-0946911123ea?w=1800&q=85', title:'Kitchens That', titleEm:'Delight', subtitle:'Modular kitchens engineered for modern Indian living with premium fittings and smart storage.' },
  { image:'https://images.unsplash.com/photo-1586023492125-27b2c045efd7?w=1800&q=85', title:'Ceilings That', titleEm:'Elevate', subtitle:'False ceilings and ambient lighting that transform the feel of every room.' },
];

const SERVICES = [
  { icon:'🗄️', title:'Complete Modular Works',   path:'/services/complete-modular-site-work', desc:'Factory-precision modular units installed with complete site execution from demolition to handover.' },
  { icon:'🛋️', title:'Interior Design',                path:'/services/interior-design',            desc:'Bespoke interiors born from your vision — 3D design, material curation, and flawless execution.' },
  { icon:'💡', title:'Home Lighting',                  path:'/services/home-lighting',              desc:'Curated lighting schemes that define ambiance — from statement chandeliers to intimate cove light.' },
  { icon:'🍳', title:'Modular Kitchen',                path:'/services/modular-kitchen',            desc:'Smart, space-maximising kitchens with premium countertops, hardware and innovative storage.' },
  { icon:'🎨', title:'False Ceiling & Painting',       path:'/services/false-ceiling-painting',     desc:'Gypsum, POP and cove ceiling designs paired with premium wall finishes and texture paints.' },
  { icon:'🪟', title:'Aluminium & Glass Works',        path:'/services/aluminium-glass-works',      desc:'Contemporary glass partitions, shower enclosures and aluminium doors for modern homes.' },
];

const PROCESS = [
  { num:'01', title:'Consultation', desc:'Free meeting with our design experts to understand your space, lifestyle, and budget.' },
  { num:'02', title:'3D Design',    desc:'Photorealistic 3D renders of your space before a single nail is hammered.' },
  { num:'03', title:'Finalize',     desc:'Approve the design, sign off on materials, and pay just 5% to begin production.' },
  { num:'04', title:'Execute',      desc:'Our craftsmen execute with full transparency, site visits, and quality checks.' },
  { num:'05', title:'Handover',     desc:`Deep-clean handover with a ${WARRANTY_YEARS}-year warranty certificate on all our work.` },
];

export default function Home() {
  const [banners, setBanners]       = useState([]);
  const [portfolio, setPortfolio]   = useState([]);
  const [gallery, setGallery]       = useState([]);

  const [social, setSocial]         = useState([]);
  const [testimonials, setTest]     = useState([]);
  const [stats, setStats]           = useState([]);
  const [slide, setSlide]           = useState(0);
  const timerRef = useRef(null);

  useEffect(() => {
    api.get('/banners').then(r => setBanners(Array.isArray(r.data)?r.data:(r.data?.data||[]))).catch(()=>{});
    api.get('/portfolio?featured=true').then(r => { const d=Array.isArray(r.data)?r.data:(r.data?.data||[]); setPortfolio(d.slice(0,6)); }).catch(()=>{});
    api.get('/gallery').then(r => { const d=Array.isArray(r.data)?r.data:(r.data?.data||[]); setGallery(d.slice(0,6)); }).catch(()=>{});
    api.get('/social').then(r => { const d=Array.isArray(r.data)?r.data:(r.data?.posts||r.data?.data||[]); setSocial(d.slice(0,4)); }).catch(()=>{});
    api.get('/testimonials').then(r => { const d=Array.isArray(r.data)?r.data:(r.data?.data||[]); setTest(d.slice(0,3)); }).catch(()=>{});
    api.get('/stats').then(r => { const d=Array.isArray(r.data)?r.data:(r.data?.data||[]); setStats(d); }).catch(()=>{});
  }, []);

  const slides = banners.length > 0 ? banners : FALLBACK_SLIDES;
  useEffect(() => {
    timerRef.current = setInterval(() => setSlide(p => (p+1) % slides.length), 6000);
    return () => clearInterval(timerRef.current);
  }, [slides.length]);

  const defaultStats = STAT_ITEMS;
  const displayStats = stats.length > 0 ? stats.map(s => ({ value:s.value, label:s.label })) : defaultStats;

  return (
    <>
      <Helmet>
        <title>Wedoo Interiors | Best Interior Designers in Tumakuru, Karnataka</title>
        <meta name="description" content="Wedoo Interiors — Premium interior design studio in Tumakuru, Karnataka. Modular kitchens, false ceilings, home lighting & complete interior solutions. Get your free consultation today." />
        <meta name="keywords" content="wedoo interiors, wedoointeriors, interior design tumakuru, modular kitchen tumakuru, interior designers karnataka" />
      </Helmet>

      {/* ══════════ HERO ══════════ */}
      <section style={{ position:'relative', height:'100vh', minHeight:'640px', overflow:'hidden' }}>
        {/* Slides */}
        {slides.map((sl, i) => (
          <div key={i} style={{
            position:'absolute', inset:0, transition:'opacity 1.4s ease',
            opacity: i === slide ? 1 : 0,
            background:`url(${imgSrc(sl.image)}) center/cover no-repeat`,
          }} />
        ))}
        <div className="hero-overlay" />

        {/* Decorative ring */}
        <div style={{ position:'absolute', top:'15%', right:'8%', width:'300px', height:'300px', border:'1px solid rgba(245,168,0,0.1)', borderRadius:'50%', animation:'spin 40s linear infinite', pointerEvents:'none' }} />
        <div style={{ position:'absolute', top:'20%', right:'13%', width:'180px', height:'180px', border:'1px solid rgba(245,168,0,0.07)', borderRadius:'50%', animation:'spin 25s linear infinite reverse', pointerEvents:'none' }} />

        {/* Content */}
        <div className="container" style={{ position:'relative', zIndex:2, height:'100%', display:'flex', alignItems:'center' }}>
          <div style={{ maxWidth:'700px' }}>
            <div className="fade-up" style={{ display:'flex', alignItems:'center', gap:'0.75rem', marginBottom:'1.25rem' }}>
              <div style={{ width:'32px', height:'1px', background:'#F5A800' }} />
              <span style={{ fontSize:'0.65rem', letterSpacing:'0.25em', textTransform:'uppercase', color:'#F5A800' }}>Premium Design Studio — Tumakuru</span>
            </div>
            <h1 className="fade-up-1" style={{ fontFamily:'var(--font-display)', fontSize:'clamp(3rem,7vw,5.5rem)', fontWeight:600, lineHeight:1.0, color:'#f8f5ef', marginBottom:'1.75rem', letterSpacing:'-0.02em' }}>
              {slides[slide]?.title || 'Spaces That'}{' '}
              <em style={{ color:'#F5A800', fontStyle:'italic' }}>{slides[slide]?.titleEm || 'Inspire'}</em>
            </h1>
            <p className="fade-up-2" style={{ color:'rgba(248,245,239,0.75)', fontSize:'1rem', lineHeight:'1.8', maxWidth:'520px', marginBottom:'2.5rem' }}>
              {slides[slide]?.subtitle || 'Premium interior design crafted for the way you live — in Tumakuru & across Karnataka.'}
            </p>
            <div className="fade-up-3" style={{ display:'flex', gap:'1rem', flexWrap:'wrap' }}>
              <Link to="/get-free-quote" className="btn btn-gold">Book Free Consultation</Link>
              <Link to="/portfolio" className="btn btn-ghost">View Portfolio</Link>
            </div>
          </div>
        </div>

        {/* Slide controls */}
        <div style={{ position:'absolute', bottom:'2.5rem', right:'2rem', display:'flex', gap:'0.5rem', zIndex:3 }}>
          {slides.map((_,i) => (
            <button key={i} onClick={() => { setSlide(i); clearInterval(timerRef.current); }} style={{
              width: i===slide ? '28px' : '8px', height:'2px',
              background: i===slide ? '#F5A800' : 'rgba(255,255,255,0.3)',
              border:'none', cursor:'pointer', transition:'all 0.4s', borderRadius:'1px'
            }} />
          ))}
        </div>

        {/* Scroll hint */}
        <div style={{ position:'absolute', bottom:'2.5rem', left:'50%', transform:'translateX(-50%)', display:'flex', flexDirection:'column', alignItems:'center', gap:'0.5rem', zIndex:3 }}>
          <div style={{ width:'1px', height:'44px', background:'linear-gradient(to bottom, transparent, rgba(245,168,0,0.6))', animation:'fadeIn 2s ease 1s both' }} />
          <span style={{ fontSize:'0.58rem', letterSpacing:'0.22em', textTransform:'uppercase', color:'rgba(245,168,0,0.6)' }}>Scroll</span>
        </div>
      </section>

      {/* ══════════ STATS BAR ══════════ */}
      <section style={{ background:'#141414', borderTop:'1px solid rgba(245,168,0,0.12)', borderBottom:'1px solid rgba(245,168,0,0.12)' }}>
        <div className="container">
          <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', padding:'2.5rem 0' }} className="stats-grid">
            {displayStats.map((s,i) => (
              <div key={i} style={{ textAlign:'center', padding:'1rem', borderRight: i<3 ? '1px solid rgba(255,255,255,0.06)' : 'none' }}>
                <div className="stat-number">{s.value}</div>
                <div style={{ fontSize:'0.66rem', letterSpacing:'0.18em', textTransform:'uppercase', color:'#8a8278', marginTop:'0.4rem' }}>{s.label}</div>
              </div>
            ))}
          </div>
        </div>
        <style>{`@media(max-width:640px){.stats-grid{grid-template-columns:repeat(2,1fr)!important}}`}</style>
      </section>

      {/* ══════════ SERVICES ══════════ */}
      <section className="section-pad" style={{ background:'#0c0c0c' }}>
        <div className="container">
          <SectionHeader eyebrow="What We Do" title={<>Our <em style={{color:'#F5A800',fontStyle:'italic'}}>Services</em></>} subtitle="End-to-end interior solutions crafted with precision — from concept to completion, backed by a 5-year warranty." center />
          <div className="grid-3" style={{ gap:'1.5px', background:'rgba(245,168,0,0.08)' }}>
            {SERVICES.map(s => (
              <Link key={s.title} to={s.path} className="service-card" style={{ textDecoration:'none' }}>
                <div style={{ fontSize:'2.2rem', marginBottom:'1.25rem' }}>{s.icon}</div>
                <h3 style={{ fontFamily:'var(--font-display)', fontSize:'1.3rem', fontWeight:400, color:'#f8f5ef', marginBottom:'0.85rem', lineHeight:1.2 }}>{s.title}</h3>
                <p style={{ color:'#8a8278', fontSize:'0.82rem', lineHeight:'1.75', marginBottom:'1.5rem' }}>{s.desc}</p>
                <span style={{ fontSize:'0.65rem', letterSpacing:'0.18em', textTransform:'uppercase', color:'#F5A800' }}>Explore →</span>
              </Link>
            ))}
          </div>
          <div style={{ textAlign:'center', marginTop:'3rem' }}>
            <Link to="/services" className="btn btn-outline">View All Services</Link>
          </div>
        </div>
      </section>

      {/* ══════════ ABOUT SPLIT ══════════ */}
      <section className="section-pad" style={{ background:'#141414' }}>
        <div className="container">
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'6rem', alignItems:'center' }} className="about-split">
            <div>
              <SectionHeader eyebrow="Our Story" title={<>Designing Dreams<br/><em style={{color:'#F5A800',fontStyle:'italic'}}>Since 2021</em></>} subtitle="Wedoo Interiors is Tumakuru's premier interior design studio — combining artistic vision with technical mastery to create spaces that truly reflect who you are." />
              <div style={{ display:'flex', flexDirection:'column', gap:'0.9rem', marginBottom:'2.5rem' }}>
                {[`${WARRANTY_YEARS}-year warranty on every project`,'Transparent pricing — no hidden costs',`${COMPANY_FACTS.projects} completed projects across Karnataka`,'2030-forward design aesthetics'].map(t => (
                  <div key={t} style={{ display:'flex', alignItems:'center', gap:'0.85rem', color:'#ede8de', fontSize:'0.86rem' }}>
                    <span style={{ width:'6px', height:'6px', background:'#F5A800', borderRadius:'50%', flexShrink:0 }} />
                    {t}
                  </div>
                ))}
              </div>
              <Link to="/about" className="btn btn-gold">Discover Our Story</Link>
            </div>
            <div style={{ position:'relative' }}>
              <img src="https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=800&q=85" alt="Wedoo Interiors Design Studio" loading="lazy" decoding="async" width="800" height="520" style={{ width:'100%', height:'520px', objectFit:'cover' }} />
              <div style={{ position:'absolute', bottom:'-2rem', left:'-2rem', background:'#F5A800', padding:'1.75rem 2.25rem' }}>
                <div style={{ fontFamily:'var(--font-display)', fontSize:'2.5rem', fontWeight:700, color:'#0c0c0c', lineHeight:1 }}>{YEARS_ACTIVE}</div>
                <div style={{ fontSize:'0.6rem', letterSpacing:'0.2em', textTransform:'uppercase', color:'rgba(12,12,12,0.7)', marginTop:'0.3rem' }}>Years of Excellence</div>
              </div>
              <div style={{ position:'absolute', top:'-1.5rem', right:'-1.5rem', width:'120px', height:'120px', border:'1px solid rgba(245,168,0,0.3)', borderRadius:'var(--radius)' }} />
            </div>
          </div>
        </div>
        <style>{`@media(max-width:860px){.about-split{grid-template-columns:1fr!important;gap:3rem!important}}`}</style>
      </section>

      {/* ══════════ PORTFOLIO PREVIEW ══════════ */}
      {portfolio.length > 0 && (
        <section className="section-pad" style={{ background:'#0c0c0c' }}>
          <div className="container">
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-end', marginBottom:'3rem', flexWrap:'wrap', gap:'1rem' }}>
              <SectionHeader eyebrow="Our Work" title={<>Featured <em style={{color:'#F5A800',fontStyle:'italic'}}>Projects</em></>} subtitle="" />
              <Link to="/portfolio" className="btn btn-outline" style={{ flexShrink:0 }}>All Projects →</Link>
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:'4px' }} className="port-grid">
              {portfolio.slice(0,6).map((p,i) => (
                <div key={p._id} className="port-item" style={{ height: i===0 || i===3 ? '420px' : '280px' }}>
                  <img src={imgSrc(p.images?.[0])} alt={p.title} loading="lazy" decoding="async" />
                  <div className="port-overlay">
                    <span className="badge badge-gold" style={{ marginBottom:'0.5rem', alignSelf:'flex-start' }}>{p.category?.replace(/-/g,' ')}</span>
                    <h3 style={{ fontFamily:'var(--font-display)', fontSize:'1.2rem', fontWeight:400, color:'#f8f5ef' }}>{p.title}</h3>
                    {p.location && <p style={{ color:'#8a8278', fontSize:'0.72rem', marginTop:'0.25rem' }}>📍 {p.location}</p>}
                  </div>
                </div>
              ))}
            </div>
          </div>
          <style>{`@media(max-width:640px){.port-grid{grid-template-columns:1fr!important}}`}</style>
        </section>
      )}

      {/* ══════════ END-TO-END SOLUTIONS ══════════ */}
      <EndToEndSolutions />

      {/* ══════════ GOOGLE REVIEWS ══════════ */}
      <GoogleReviews />

      {/* ══════════ PROCESS ══════════ */}
      <section className="section-pad" style={{ background:'#141414' }}>
        <div className="container">
          <SectionHeader eyebrow="How We Work" title={<>Our Design <em style={{color:'#F5A800',fontStyle:'italic'}}>Process</em></>} subtitle="A clear, transparent journey from first meeting to final handover — with no surprises." center />
          <div style={{ display:'grid', gridTemplateColumns:'repeat(5,1fr)', gap:'0', position:'relative' }} className="process-grid">
            <div style={{ position:'absolute', top:'28px', left:'10%', right:'10%', height:'1px', background:'linear-gradient(to right, transparent, rgba(245,168,0,0.3), transparent)', zIndex:0 }} className="process-line" />
            {PROCESS.map((p,i) => (
              <div key={p.num} style={{ textAlign:'center', padding:'1rem 0.75rem', position:'relative', zIndex:1 }}>
                <div style={{ width:'56px', height:'56px', border:'1px solid #F5A800', borderRadius:'50%', display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 1.25rem', background:'#141414', fontFamily:'var(--font-display)', fontSize:'1.1rem', color:'#F5A800' }}>{p.num}</div>
                <h4 style={{ fontSize:'0.85rem', fontWeight:500, color:'#f8f5ef', marginBottom:'0.6rem', letterSpacing:'0.04em' }}>{p.title}</h4>
                <p style={{ color:'#8a8278', fontSize:'0.76rem', lineHeight:'1.65' }}>{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
        <style>{`@media(max-width:768px){.process-grid{grid-template-columns:1fr!important}.process-line{display:none!important}}`}</style>
      </section>

      {/* ══════════ TESTIMONIALS ══════════ */}
      {testimonials.length > 0 && (
        <section className="section-pad" style={{ background:'#0c0c0c' }}>
          <div className="container">
            <SectionHeader eyebrow="Client Stories" title={<>What Our Clients <em style={{color:'#F5A800',fontStyle:'italic'}}>Say</em></>} center />
            <div className="grid-3">
              {testimonials.map(t => (
                <div key={t._id} className="testi-card">
                  <div style={{ display:'flex', gap:'0.25rem', marginBottom:'1rem', marginTop:'0.5rem' }}>
                    {[...Array(t.rating||5)].map((_,i) => <span key={i} style={{ color:'#F5A800', fontSize:'0.85rem' }}>★</span>)}
                  </div>
                  <p style={{ color:'#ede8de', fontSize:'0.88rem', lineHeight:'1.8', marginBottom:'1.5rem', fontStyle:'italic' }}>"{t.review}"</p>
                  <div style={{ display:'flex', alignItems:'center', gap:'0.75rem' }}>
                    <div style={{ width:'40px', height:'40px', borderRadius:'50%', background:'rgba(245,168,0,0.15)', border:'1px solid rgba(245,168,0,0.2)', display:'flex', alignItems:'center', justifyContent:'center', fontFamily:'var(--font-display)', color:'#F5A800', fontSize:'1.1rem' }}>
                      {t.name?.[0]}
                    </div>
                    <div>
                      <div style={{ fontSize:'0.84rem', color:'#f8f5ef', fontWeight:500 }}>{t.name}</div>
                      <div style={{ fontSize:'0.7rem', color:'#8a8278' }}>{t.location} {t.projectType && `· ${t.projectType}`}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ══════════ SOCIAL FEED ══════════ */}
      {social.length > 0 && (
        <section className="section-pad" style={{ background:'#141414' }}>
          <div className="container">
            <SectionHeader eyebrow="Social Media" title={<>Follow Our <em style={{color:'#F5A800',fontStyle:'italic'}}>Journey</em></>} subtitle="Inspiring designs shared daily on Instagram & Facebook." center />
            <div className="grid-4" style={{ gap:'4px' }}>
              {social.map(post => (
                <a key={post._id} href={post.postUrl} target="_blank" rel="noopener noreferrer" className="port-item" style={{ height:'260px', display:'block' }}>
                  {post.thumbnail
                    ? <img src={post.thumbnail} alt={post.caption} loading="lazy" decoding="async" style={{ width:'100%', height:'100%', objectFit:'cover' }} />
                    : <div style={{ height:'100%', background:'#1c1c1c', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'2.5rem' }}>{post.platform==='instagram'?'📷':'👍'}</div>
                  }
                  <div className="port-overlay" style={{ paddingBottom:'1rem' }}>
                    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-end' }}>
                      <span style={{ color:'#F5A800', fontSize:'1.2rem' }}>{post.platform==='instagram'?'📷':'👍'}</span>
                      {post.likes && <span style={{ color:'rgba(255,255,255,0.5)', fontSize:'0.7rem' }}>❤ {post.likes}</span>}
                    </div>
                  </div>
                </a>
              ))}
            </div>
            <div style={{ textAlign:'center', marginTop:'2.5rem', display:'flex', gap:'1rem', justifyContent:'center' }}>
              <a href="https://www.instagram.com/wedoo_interiors/" target="_blank" rel="noopener noreferrer" className="btn btn-outline" style={{ fontSize:'0.68rem' }}>📷 Instagram</a>
              <a href="https://facebook.com/wedoointeriors" target="_blank" rel="noopener noreferrer" className="btn btn-dark" style={{ fontSize:'0.68rem' }}>👍 Facebook</a>
            </div>
          </div>
        </section>
      )}

      {/* ══════════ CTA BANNER ══════════ */}
      <section style={{ position:'relative', padding:'8rem 0', overflow:'hidden' }}>
        <div style={{ position:'absolute', inset:0, background:`url(https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=1600&q=80) center/cover no-repeat` }} />
        <div style={{ position:'absolute', inset:0, background:'linear-gradient(135deg, rgba(12,12,12,0.92) 0%, rgba(12,12,12,0.75) 100%)' }} />
        <div className="container" style={{ position:'relative', zIndex:2, textAlign:'center' }}>
          <span className="tag-label" style={{ justifyContent:'center', marginBottom:'1.25rem', display:'flex' }}>Start Your Journey</span>
          <h2 style={{ fontFamily:'var(--font-display)', fontSize:'clamp(2.2rem,5vw,3.8rem)', fontWeight:600, color:'#f8f5ef', marginBottom:'0.5rem' }}>
            Ready to Transform
          </h2>
          <h2 style={{ fontFamily:'var(--font-display)', fontSize:'clamp(2.2rem,5vw,3.8rem)', fontWeight:600, color:'#f8f5ef', marginBottom:'2rem' }}>
            <em style={{ color:'#F5A800' }}>Your Space?</em>
          </h2>
          <p style={{ color:'rgba(248,245,239,0.65)', fontSize:'0.95rem', maxWidth:'480px', margin:'0 auto 3rem', lineHeight:'1.8' }}>
            Book a free consultation with our design experts. We'll visit your space and deliver a vision that exceeds your expectations.
          </p>
          <div style={{ display:'flex', gap:'1rem', justifyContent:'center', flexWrap:'wrap' }}>
            <Link to="/get-free-quote" className="btn btn-gold">Book Free Consultation</Link>
            <a href="tel:+919686583195" className="btn btn-ghost">📞 +919686583195</a>
          </div>
        </div>
      </section>
    </>
  );
}
